<template>
	<div style="width: 100%" class="small-text funds-table">
		<bee-datatable
			:max-height="maxHeight"
			:item-class="itemClass"
			:zebra="zebra"
			:hide-header="hideHeader"
			:hide-footer="hideFooter"
			:headers="localHeaders"
			:items="items"
			:editable-row-index="rowIndex"
			@contextmenu="openContextMenu"
			ref="table"
			:addable="addable && !disabledValue"
			:is-funds="true"
			:highlighted-color="highlightedColor"
			:highlighted-expression="highlightedExpression"
		>
			<!-- default rows -->
			<template
				v-for="header in localHeaders"
				v-slot:[`item.${localHeaders.indexOf(header)}`]="{ item }"
			>
				<v-tooltip :key="header.id" top>
					<template v-slot:activator="{ }">
                        <!-- header type: string -->
						<div v-if="header.type == 'string'">
							<bee-text-field
								v-model="localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`]"
								:ref="`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`"
								class="input-text-center"
								:readonly="items.indexOf(item) != rowIndex || !editable || !header.editable"
								:inside-table="true"
								:multi-line="true"
								:empty-state="empty"
								:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
								:rules="header.rules"
								:prefix="header.settings ? header.settings.prefix : undefined"
								:context="context"
								:action-name="actionName"
								@input="onFocus({
                                    header: header,
                                    value: localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`],
                                    index: items.indexOf(item)
                                })"
								@keydown-enter-prevent="onEnter()"
								@keydown-esc-prevent="onEsc()"
								@keydown-tab="e => onTab(e, localHeaders.indexOf(header))"
								@blur="onBlur(header.value)"
								@keydown-ctrl-down="onEnter()"
							></bee-text-field>
						</div>
                        
                        <!-- header type: string-multi-lines -->
						<div v-if="header.type == 'string-multi-lines'">
							<v-textarea
								:readonly="items.indexOf(item) != rowIndex || !editable || !header.editable"
								:ref="`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`"
								class="input-text-center"
								@input="inputValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`] = $event"
								@blur="onFocus({header: header, value: inputValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`], index: items.indexOf(item)})"
								:value="getValueFromObject(item, header.value, true) || empty"
								:solo="items.indexOf(item) != rowIndex || !editable || !header.editable"
								@keydown.enter.prevent="onEnter()"
								@keydown.esc.prevent="onEsc()"
								@keydown.tab="e => onTab(e, localHeaders.indexOf(header))"
								multi-line
								:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
								:rules="header.rules"
								@keydown-ctrl-down="onEnter()"
							></v-textarea>
						</div>
                        
                        <!-- header type: auto_increment -->
						<div v-if="header.type == 'auto_increment'">{{ items.indexOf(item) + 1 }}</div>
                        
                        <!-- header type: date -->
						<div v-if="header.type == 'date'">
							<bee-date-field
								:ref="`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`"
								:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
								:readonly="items.indexOf(item) != rowIndex || !editable || !header.editable"
								:inside-table="true"
								v-model="localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`]"
								@input="onFocus({header: header, value: localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`], index: items.indexOf(item)})"
								@keydown-enter-prevent="onEnter()"
								@keydown-esc-prevent="onEsc()"
								@blur="onBlur(header.value)"
								@keydown-tab="e => onTab(e, localHeaders.indexOf(header))"
								@keydown-ctrl-down="onEnter()"
							></bee-date-field>
						</div>
                        
                        <!-- header type: materials-autocomplete -->
						<div v-if="header.type == 'materials-autocomplete'" style="width: 100%">
							<bee-material-autocomplete
								:ref="`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`"
								:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
								:readonly="items.indexOf(item) != rowIndex || !editable || !header.editable"
								v-model="localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`]"
								@input="onFocus({header: header, value: localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`], index: items.indexOf(item)})"
								:items="header.settings.items.filter(c=> item.warehouse_id ? c.warehouse_id == item.warehouse_id : true) || []"
								:item-text="header.settings.itemText"
								:inside-table="true"
								@keydown-enter-prevent="onEnter()"
								@keydown-esc-prevent="onEsc()"
								:rules="header.rules"
								:empty-state="empty"
								@blur="onBlur(header.value)"
								@keydown-ctrl-down="onEnter()"
								:with-balance="true"
							></bee-material-autocomplete>
						</div>
                        
                        <!-- header type: autocomplete -->
						<div v-if="header.type == 'autocomplete'" style="width: 100%">
							<bee-autocomplete
								:ref="`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`"
								:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
								:readonly="items.indexOf(item) != rowIndex || !editable || !header.editable"
								v-model="localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`]"
								@input="onFocus({header: header, value: localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`], index: items.indexOf(item)})"
								:items="header.settings.items || []"
								:item-text="header.settings.itemText"
								:item-value="header.settings.itemValue"
								:inside-table="true"
								@keydown-enter-prevent="onEnter()"
								@keydown-esc-prevent="onEsc()"
								:rules="header.rules"
								:empty-state="empty"
								@blur="onBlur(header.value)"
								@keydown-ctrl-down="onEnter()"
								:clearable="header.settings.clearable"
								:context="context"
								:action-name="actionName"
							></bee-autocomplete>
						</div>
                        
                        <!-- header type: accounts-autocomplete -->
						<div v-if="header.type == 'accounts-autocomplete'" style="width: 100%">
							<bee-account-autocomplete
								:ref="`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`"
								:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
								:readonly="items.indexOf(item) != rowIndex || !editable || !header.editable"
								v-model="localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`]"
								@input="onFocus({header: header, value: localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`], index: items.indexOf(item)})"
								:items="localConfigs.accounts || []"
								:item-text="header.settings.itemText"
								:inside-table="true"
								@keydown-enter-prevent="onEnter()"
								@keydown-esc-prevent="onEsc()"
								:rules="header.rules"
								:empty-state="empty"
								@blur="onBlur(header.value)"
								@keydown-ctrl-down="onEnter()"
								:with-balance="true"
								:context="context"
								:action-name="actionName"
							></bee-account-autocomplete>
						</div>
                        
                        <!-- header type: multi-select -->
						<div v-if="header.type == 'multi-select'" style="width: 100%">
							<bee-multi-select
								:ref="`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`"
								:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
								:readonly="items.indexOf(item) != rowIndex || !editable || !header.editable"
								v-model="localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`]"
								@input="onFocus({header: header, value: localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`], index: items.indexOf(item)})"
								:items="localConfigs.accounts || []"
								:item-text="header.settings.itemText"
								:inside-table="true"
								@keydown-enter-prevent="onEnter()"
								@keydown-esc-prevent="onEsc()"
								:rules="header.rules"
								:empty-state="empty"
								@blur="onBlur(header.value)"
								item-value="id"
								@keydown-ctrl-down="onEnter()"
							></bee-multi-select>
						</div>
                        
                        <!-- header type: currencies-autocomplete -->
						<div v-if="header.type == 'currencies-autocomplete'">
							<bee-currencies-autocomplete
								:ref="`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`"
								:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
								:readonly="items.indexOf(item) != rowIndex || !editable || !header.editable"
								v-model="localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`]"
								@input="onFocus({header: header, value: localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`], index: items.indexOf(item)})"
								:items="localConfigs.currencies || []"
								:item-text="header.settings.itemText"
								:inside-table="true"
								@keydown-enter-prevent="onEnter()"
								@keydown-esc-prevent="onEsc()"
								:rules="header.rules"
								:empty-state="empty"
								item-value="id"
								@blur="onBlur(header.value)"
								@change="onCurrencyChange()"
								@keydown-ctrl-down="onEnter()"
							></bee-currencies-autocomplete>
						</div>
                        
                        <!-- header type: money -->
						<div v-if="header.type == 'money'">
							<bee-money-field
								:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
								:readonly="items.indexOf(item) != rowIndex || !editable || !header.editable"
								:ref="`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`"
								class="input-text-center"
								:inside-table="true"
								@input="onFocus({header: header, value: localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`] , index: items.indexOf(item)})"
								v-on:keydown.enter.prevent="onEnter()"
								v-model="localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`]"
								@keydown-esc-prevent="onEsc()"
								@keydown-enter-prevent="onEnter()"
								:rules="header.rules"
								:empty-state="empty"
								@blur="onBlur(header.value)"
								@keydown-ctrl-down="onEnter()"
								:context="context"
								:action-name="actionName"
							></bee-money-field>
						</div>
                        
                        <!-- header type: numeric -->
						<div v-if="header.type == 'numeric'">
							<bee-numeric-field
								:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
								:readonly="items.indexOf(item) != rowIndex || !editable || !header.editable"
								:ref="`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`"
								class="input-text-center"
								:inside-table="true"
								@input="onFocus({header: header, value: localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`] , index: items.indexOf(item)})"
								v-on:keydown.enter.prevent="onEnter()"
								v-model="localValues[`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`]"
								@keydown-esc-prevent="onEsc()"
								@keydown-enter-prevent="onEnter()"
								:empty-state="empty"
								:rules="header.rules"
								@blur="onBlur(header.value)"
								@keydown-ctrl-down="onEnter()"
								:prefix="header.settings ? header.settings.prefix : undefined"
								:context="context"
								:action-name="actionName"
							></bee-numeric-field>
						</div>
                        
                        <!-- header type: select -->
						<div v-if="header.type == 'select'" class="justify-start">
							<bee-currencies-autocomplete
								:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
								v-model="currencyType"
								:ref="`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`"
								@keydown-ctrl-down="onEnter()"
							></bee-currencies-autocomplete>
						</div>
                        
                        <!-- header type: hint -->
						<div v-if="header.type == 'hint'">
							<bee-hint-menu
								:value="messagesSelectRowValidation"
								:minions="minion"
								:identity="items.indexOf(item)"
								:ref="`input_${localHeaders.indexOf(header)}_${items.indexOf(item)}`"
								@keydown-ctrl-down="onEnter()"
							></bee-hint-menu>
						</div>
                        
                        <!-- header type: actions -->
						<v-flex v-if="header.type == 'actions'" offset-y class="d-inline px-1">
							<bee-btn
								beeicon
								v-if="header.actions.includes('show')"
								@click="$emit('show', item)"
								type="show"
							></bee-btn>
							<bee-btn
								beeicon
								v-if="header.actions.includes('edit')"
								@click="$emit('edit', item)"
								type="edit"
							></bee-btn>
							<bee-btn
								beeicon
								v-if="header.actions.includes('equivalent_price')"
								@click="$emit('equivalent', item)"
								type="cached"
							></bee-btn>
							<bee-btn
								beeicon
								v-if="header.actions.includes('history')"
								@click="$emit('history', item)"
								type="history"
							></bee-btn>
							<bee-btn
								beeicon
								v-if="header.actions.includes('delete')"
								@click="$emit('delete', item)"
								type="delete"
							></bee-btn>
						</v-flex>
					</template>
					<span>{{ getValueFromObject(item, header.value) }}</span>
				</v-tooltip>
			</template>

			<!-- adding row -->
			<template
				v-for="(header, index) in localHeaders"
				v-slot:[`footer.${localHeaders.indexOf(header)}`]
			>
				<td v-if="addable && !disabledValue" :key="index" top style="width: 100%">
                    
                    <!-- header type: string -->
					<div v-if="header.type == 'string'">
						<bee-text-field
							:context="context"
							:action-name="actionName"
							:ref="`input_${localHeaders.indexOf(header)}`"
							:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
							@input="lastRow = true"
							@blur="onBlur(header.value)"
							:readonly="!header.editable"
							:inside-table="true"
							v-model="endLineInputValues[header.value]"
							v-on:keydown-enter-prevent="onEnter()"
							@keydown-ctrl-down="onEnter()"
							:rules="header.rules"
						></bee-text-field>
					</div>
                    
                    <!-- header type: auto_increment'">{{ items.length + 1 }}</d -->
					<div v-if="header.type == 'auto_increment'">{{ items.length + 1 }}</div>
                    
                    <!-- header type: date'" style="width: 100 -->
					<div v-if="header.type == 'date'" style="width: 100%">
						<bee-date-field
							:ref="`input_${localHeaders.indexOf(header)}`"
							:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
							:readonly="!header.editable"
							@blur="onBlur(header.value)"
							:inside-table="true"
							v-model="endLineInputValues[header.value]"
							v-on:keydown-enter-prevent="onEnter()"
							@keydown-tab="e => onTab(e, localHeaders.indexOf(header))"
							@keydown-ctrl-down="onEnter()"
						></bee-date-field>
					</div>

                    <!-- header type: accounts-autocomplete -->
					<div v-if="header.type == 'accounts-autocomplete'" style="width: 100%;">
						<bee-account-autocomplete
							:context="context"
							:action-name="actionName"
							:with-balance="true"
							:ref="`input_${localHeaders.indexOf(header)}`"
							:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
							:readonly="!header.editable"
							@blur="onBlur(header.value)"
							@input="lastRow = true"
							:rules="header.rules"
							:items="localConfigs.accounts.filter(c=>c.type == 2) || []"
							:inside-table="true"
							v-model="endLineInputValues[header.value]"
							:item-text="header.settings.itemText"
							style="width: 100%;"
							v-on:keydown-enter-prevent="onEnter()"
							@keydown-ctrl-down="onEnter()"
						></bee-account-autocomplete>
					</div>


                    <!-- header type: materials-autocomplete -->
					<div v-if="header.type == 'materials-autocomplete'" style="width: 100%;">
						<bee-material-autocomplete
							:with-balance="true"
							:ref="`input_${localHeaders.indexOf(header)}`"
							:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
							:readonly="!header.editable"
							@blur="onBlur(header.value)"
							@input="lastRow = true"
							:rules="header.rules"
							:items="header.settings.subItems || []"
							:inside-table="true"
							v-model="endLineInputValues[header.value]"
							:item-text="header.settings.itemText"
							style="width: 100%;"
							v-on:keydown-enter-prevent="onEnter()"
							@keydown-ctrl-down="onEnter()"
						></bee-material-autocomplete>
					</div>

                    <!-- header type: autocomplete -->
					<div v-if="header.type == 'autocomplete'" style="width: 100%;">
						<bee-autocomplete
							:context="context"
							:action-name="actionName"
							:ref="`input_${localHeaders.indexOf(header)}`"
							:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
							:readonly="!header.editable"
							@blur="onBlur(header.value)"
							@input="lastRow = true"
							:rules="header.rules"
							:items="header.settings.items || []"
							:inside-table="true"
							v-model="endLineInputValues[header.value]"
							:item-value="header.settings.itemValue"
							:item-text="header.settings.itemText"
							style="width: 100%;"
							v-on:keydown-enter-prevent="onEnter()"
							@keydown-ctrl-down="onEnter()"
							:clearable="header.settings.clearable"
						></bee-autocomplete>
					</div>


                    <!-- header type: money -->
					<div v-if="header.type == 'money'" style="font-size:16px !important">
						<bee-money-field
							:context="context"
							:action-name="actionName"
							:ref="`input_${localHeaders.indexOf(header)}`"
							:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
							:readonly="!header.editable"
							@blur="onBlur(header.value)"
							@input="lastRow = true"
							:inside-table="true"
							v-model="endLineInputValues[header.value]"
							:rules="header.rules"
							v-on:keydown-enter-prevent="onEnter()"
							@keydown-ctrl-down="onEnter()"
						></bee-money-field>
					</div>
                    
                    <!-- header type: numeric -->
					<div v-if="header.type == 'numeric'">
						<bee-numeric-field
							:context="context"
							:action-name="actionName"
							:prefix="header.settings ? header.settings.prefix : undefined"
							:ref="`input_${localHeaders.indexOf(header)}`"
							:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
							:readonly="!header.editable"
							@blur="onBlur(header.value)"
							@input="lastRow = true"
							:inside-table="true"
							v-model="endLineInputValues[header.value]"
							:rules="header.rules"
							v-on:keydown-enter-prevent="onEnter()"
							@keydown-ctrl-down="onEnter()"
						></bee-numeric-field>
					</div>
                    
                    <!-- header type: currencies-autocomplete -->
					<div v-if="header.type == 'currencies-autocomplete'">
						<bee-currencies-autocomplete
							@input="lastRow = true"
							@blur="onBlur(header.value)"
							v-model="endLineInputValues[header.value]"
							:items="localConfigs.currencies || []"
							:item-text="header.settings.itemText"
							:inside-table="true"
							:rules="header.rules"
							@change="onCurrencyChange()"
							v-on:keydown-enter-prevent="onEnter()"
							@keydown-ctrl-down="onEnter()"
							:disabled="loading || (header.settings ? Boolean(header.settings.disabled) : false)"
							:readonly="!header.editable"
							:ref="`input_${localHeaders.indexOf(header)}`"
						></bee-currencies-autocomplete>
					</div>
                    
                    <!-- header type: hint -->
					<div v-if="header.type == 'hint'">
						<bee-hint-menu
							:value="messagesSelectRowValidation"
							:minions="minion"
							:identity="-1"
							:ref="`input_${localHeaders.indexOf(header)}`"
							@keydown-ctrl-down="onEnter()"
						></bee-hint-menu>
					</div>
                    
                    <!-- header type: actions -->
					<v-flex v-if="header.type == 'actions'" offset-y class="d-inline px-1">
						<bee-btn
							:context="context"
							:action-name="header.actions.find(c=>c.name == 'show').action_name"
							beeicon
							v-if="header.actions.includes('show')"
							@click="$emit('show', item)"
							type="show"
						></bee-btn>
						<bee-btn
							:context="context"
							:action-name="header.actions.find(c=>c.name == 'edit').action_name"
							beeicon
							v-if="header.actions.includes('edit')"
							@click="$emit('edit', item)"
							type="edit"
						></bee-btn>
						<bee-btn
							:context="context"
							:action-name="header.actions.find(c=>c.name == 'equivalent_price').action_name"
							beeicon
							v-if="header.actions.includes('equivalent_price')"
							@click="$emit('equivalent', item)"
							type="cached"
						></bee-btn>
						<bee-btn
							:context="context"
							:action-name="header.actions.find(c=>c.name == 'history').action_name"
							beeicon
							v-if="header.actions.includes('history')"
							@click="$emit('history', item)"
							type="history"
						></bee-btn>
						<bee-btn
							:context="context"
							:action-name="header.actions.find(c=>c.name == 'delete').action_name"
							beeicon
							v-if="header.actions.includes('delete')"
							@click="$emit('delete', item)"
							type="delete"
						></bee-btn>
					</v-flex>
				</td>
			</template>
		</bee-datatable>
		<v-menu v-model="menu" :position-x="mouse.x" :position-y="mouse.y" absolute offset-y>
			<bee-list v-if="showMenuActions">
				<div v-for="(item, i) in rightClickMenuItems" :key="i">
					<bee-list-item
						:item="item"
						:context="context"
						@click="item.name == 'edit' ? rowIndex = items.indexOf(rightClickItem) : $emit(`${item.name}`, rightClickItem)"
					></bee-list-item>
				</div>
			</bee-list>
		</v-menu>
	</div>
</template>

<script>
import baseBeeComponent from "../baseBeeComponent";
import EventBus from "../../../event-bus";
import _ from "lodash";
import { setDebitCreditValue, refactorEntry } from "../../features/journals/helper/operations-calculated-properties.js";
import { updateCalculatedProps, setLastPriceForMaterial } from "../../features/Invoices/helper/operations-calculated-properties.js";
import { updateCalculated, setLastPriceForMaterialManufacturing } from "../../features/manufacturing/helper/operations-calculated-properties";
export default {
	mixins: [baseBeeComponent],
	props: [
		"items",
		"headers",
		"empty-state",
		"hide-header",
		"hide-footer",
		"with-color",
		"addable",
		"editable",
		"configs",
		"show-menu-actions",
		"right-click-menu-items",
		"zebra",
		"itemClass",
		"messagesSelectRowValidation",
		"highlighted-color",
		"highlighted-expression",
		"mode",
		"max-height",
	],
	data: () => ({
		emptyStrings: [
			{
				string: "_",
				type: "dashes"
			},
			{
				string: "",
				type: "none"
			}
		],
		currencyType: null,
		inputValues: {},
		endLineInputValues: {},
		rowIndex: null,
		account: null,
		menu: false,
		mouse: { x: 0, y: 0 },
		rightClickItem: null,
		dialogDeleteAccountingEntry: false,
		dialogDeleteCashEntry: false,
		dialogAccountMatching: false,
		numericInputValue: null,
		lastRow: false,
		loading: false
	}),
	computed: {
		empty() {
			return this.emptyStrings.find(es => es.type == this.emptyState).string;
		},
		localValues() {
			return this.assigneTolocalValues();
		},
		localItems: {
			get: function() {
				return this.items;
			},
			set: function(value) {
				this.items = value;
			}
		},
		localConfigs: {
			get: function() {
				return this.configs;
			},
			set: function(value) {
				this.configs = value;
			}
		},
		localHeaders: {
			get: function() {
				return this.headers;
			},
			set: function(value) {
				this.headers = value;
			}
		},
		minion() {
			if (this.rowIndex == 0 || this.rowIndex) return this.rowIndex;
			if (this.lastRow) return -1; //identity of last row
			return null;
		},
	},
	methods: {
		openLoading() {
			this.$refs.table.openLoading();
		},
		closeLoading() {
			this.$refs.table.closeLoading();
		},
		assigneTolocalValues() {
			this.localItems.forEach(item => {
				this.localHeaders.forEach(header => {
					this.inputValues[
						`input_${this.localHeaders.indexOf(header)}_${this.items.indexOf(
							item
						)}`
					] = this.getValueFromObject(item, header.value);
				});
			});
			return this.inputValues;
		},
		getValueFromObject(object, header, isFirst = false) {
			if (header == "") return isFirst ? "" : object;
			let path = (header || "").split(".");
			object = object[path[0]];
			path.shift();
			return object &&
				typeof object === "object" &&
				object.constructor === Object
				? this.getValueFromObject(object, path.join("."))
				: object;
		},
		onEnter() {
			if (this.rowIndex == 0 || this.rowIndex) {
				this.$emit("nblur", this.localItems[this.rowIndex]);
			} else if (this.lastRow) {
				if (this.mode == "funds") {
					this.endLineInputValues = refactorEntry(
						this.endLineInputValues,
						this.localConfigs.financial_file.default_currency_id
					);
					this.$emit("nblur", this.endLineInputValues);
				}
				if (this.mode == "materials" || this.mode == "materials-journal" || this.mode == "manufacturing-raw-materials") {
					this.$emit("nblur", this.endLineInputValues);
				}
			}
			this.$nextTick(() => {
				if (this.messagesSelectRowValidation == undefined) return;
				if (this.messagesSelectRowValidation.length > 0) return;

				if (this.rowIndex == 0 || this.rowIndex) {
					this.loading = true;
					this.$emit("row-edited", {
						rowIndex: this.rowIndex,
						//sould call before connectionEnded
						deleteRowIndex: () => {
							this.rowIndex =
								this.rowIndex + 1 < this.items.length
									? this.rowIndex + 1
									: null;
						},
						connectionEnded: (
							cellIndex = 1,
							PascalComponentName = "BeeAccountAutocomplete"
						) => {
							this.loading = false;
							if (this.rowIndex == null) {
								this.$nextTick(() => {
									if (this.mode == "funds")
										this.$refs[`input_${cellIndex}`][0].$refs[
											PascalComponentName
										].$refs.BeeAutocomplete.focus();

									if (
										this.mode == "materials" ||
										this.mode == "materials-journal" ||
										this.mode == "manufacturing-raw-materials"
										
									)
										this.$refs[`input_${cellIndex}`][0].$refs[
											PascalComponentName
										].focus();
								});
								return;
							}
							if (this.rowIndex < this.items.length) {
								this.rightClickItem = this.items[this.rowIndex];
								this.$nextTick(() => {
									if (this.mode == "funds")
										this.$refs[`input_${cellIndex}_${this.rowIndex}`][0].$refs[
											PascalComponentName
										].$refs.BeeAutocomplete.focus();

									if (
										this.mode == "materials" ||
										this.mode == "materials-journal" ||
										this.mode == "manufacturing-raw-materials"
									)
										this.$refs[`input_${cellIndex}_${this.rowIndex}`][0].$refs[
											PascalComponentName
										].focus();
								});
							}
						}
					});
				} else if (this.lastRow) {
					this.loading = true;
					this.$emit("row-added", {
						value: this.endLineInputValues,
						deleteRowData: endLineInputValues => {
							this.endLineInputValues = endLineInputValues;
							this.lastRow = false;
							document
								.querySelectorAll("#table-rows")
								.forEach(rows => (rows.scrollTop = 1000000000));
						},
						connectionEnded: (cellIndex = 1, PascalComponentName = "BeeAccountAutocomplete") => {
							this.loading = false;
							this.$nextTick(() => {
								if (this.mode == "funds")
									this.$refs[`input_${cellIndex}`][0].$refs[
										PascalComponentName
									].$refs.BeeAutocomplete.focus();

								if (this.mode == "materials" ||this.mode == "materials-journal" || this.mode === 'materials-journal')
									this.$refs[`input_${cellIndex}`][0].$refs[
										PascalComponentName
									].focus();
							});
						}
					});
				}
			});
		},
		onTab(e, index) {
			if (index == this.localHeaders.length - 2) {
				this.onEnter();
			}
		},
		onEsc() {
			if (this.rowIndex != null)
				this.$emit("reset-data", this.localItems[this.rowIndex]);
			this.rowIndex = null;
			this.lastRow = null;
		},
		onFocus(value) {
			if (this.rowIndex == value.index) this.$emit("cell-change", value);
		},
		openContextMenu(e, item) {
			this.rightClickItem = item;
			e.preventDefault();
			this.menu = false;
			this.mouse.x = e.clientX;
			this.mouse.y = e.clientY;
			this.$nextTick(() => {
				this.menu = true;
			});
		},
		onBlur(header) {
			if (this.mode == "funds") {
				// عند تحديد العملة => وضع سعر العملة
				// وضع سعر العملة  => تحديث المدين والدائن
				// عند تغيير العملة المدينة أو الدائنة => تحديث المدين والدائن
				// تحديث المدين = السعر * العملة المدينة والعكس بالعكس من أجل الدائنة
				//  عند تعديل المدين أو الدائن ويوجد قيم (عملة مدينة/دائنة - العملة - السعر) => حذف مُحتوى العملة المدينة أو الدائنة
				//
				// عند تحديث عملة مدينة أودائنة إما يحُدث المدين فقط أو الدائن فقط وفي حال وجود كلاهما يحدث المدين فقط
				// مراعاة إدخال السعر بدون عملات دائنة ومدينة
				// مراعاو إدخال عملات دائنة و مدينة بدون سعر
				// لا يجوز بأي حال من الأحوال حذف السعر لأنهُ مُدخل من قبل المُستخدم  وهو قيمة غير مُرتجعة
				if (this.rowIndex == 0 || this.rowIndex) {
					setDebitCreditValue(
						this.localItems[this.rowIndex],
						this.localConfigs,
						header
					);
					this.$emit("nblur", this.localItems[this.rowIndex]);
				} else if (this.lastRow) {
					this.endLineInputValues = refactorEntry(
						this.endLineInputValues,
						this.localConfigs.financial_file.default_currency_id
					);
					setDebitCreditValue(
						this.endLineInputValues,
						this.localConfigs,
						header
					);
					this.$emit("nblur", this.endLineInputValues);
				}
			}
			if (this.mode == "materials") {
				if (this.rowIndex == 0 || this.rowIndex) {
					updateCalculatedProps(this.localItems[this.rowIndex], header);
					this.$emit("nblur", this.localItems[this.rowIndex]);
				} else if (this.lastRow) {
					updateCalculatedProps(this.endLineInputValues, header);
					this.$emit("nblur", this.endLineInputValues);
				}
			}
			if (this.mode == "materials-journals") {
				if (this.rowIndex == 0 || this.rowIndex) {
					updateCalculatedProps(this.localItems[this.rowIndex], header);
					setLastPriceForMaterial(this.localItems[this.rowIndex], header);
					this.$emit("nblur", this.localItems[this.rowIndex]);
				} else if (this.lastRow) {
					updateCalculatedProps(this.endLineInputValues, header);
					setLastPriceForMaterial(this.endLineInputValues, header);
					this.$set(this.endLineInputValues, 'price', this.endLineInputValues['price']);
					this.$emit("nblur", this.endLineInputValues);
				}
			}
			if (this.mode == "manufacturing-raw-materials"){
				if (this.rowIndex == 0 || this.rowIndex) {
					setLastPriceForMaterialManufacturing(this.localItems[this.rowIndex], header);
					this.$emit("nblur", this.localItems[this.rowIndex]);
				} else if (this.lastRow) {
					updateCalculated(this.endLineInputValues, header);
					setLastPriceForMaterialManufacturing(this.endLineInputValues, header);
					this.$set(this.endLineInputValues, 'cost', this.endLineInputValues['cost']);
					this.$set(this.endLineInputValues, 'total_cost', this.endLineInputValues['total_cost']);
					this.$emit("nblur", this.endLineInputValues);
				}
			}
		},
		onCurrencyChange() {
			if (this.rowIndex == 0 || this.rowIndex) {
				var entry = this.localItems[this.rowIndex];
				if (entry.currency_price) entry.currency_price = null;
			} else if (this.lastRow) {
				this.endLineInputValues.currency_price = null;
			}
		}
	},
	mounted() {
		EventBus.$on("initialize-last-row", $event => {
			Object.entries($event).forEach(([key, value]) => {
				let previously_added = false;
				Object.keys(this.endLineInputValues).forEach(item => {
					if (key == item) {
						previously_added = true;
						return;
					}
				});
				if (previously_added) _.set(this.endLineInputValues, key, value);
				// assign
				else this.endLineInputValues[key] = value; // add
			});
		});
		window.asd = this;
	}
};
</script>

<style>
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
	-webkit-appearance: none;
	-moz-appearance: none;
	appearance: none;
	margin: 0;
}

.input-text-center input,
.input-text-center {
	text-align: center;
}

@media (max-width: 1450px) and (min-width: 575px) {
	.small-text * {
		font-size: 0.92rem !important;
	}
}

.funds-table .v-text-field.v-text-field--enclosed .v-text-field__details {
	display: none !important;
}

.funds-table p {
	margin-bottom: 0px !important;
}
</style>